/* BRIGITTE reference-ease comparison v197. Product-specific; no storage or requests. */
(function (root) {
 'use strict';
 const EPS = 0.000002;
 const BODY_KEYS = ['bust', 'waist', 'hip'];
 function parseMeasurement(value) {
  const raw = String(value ?? '').trim();
  if (!/^\d+(?:[.,]\d+)?$/.test(raw)) return null;
  const number = Number(raw.replace(',', '.'));
  return Number.isFinite(number) && number > 0 ? number : null;
 }
 function referenceEase(data, index) {
  const anchors = data.reference_fit.anchors;
  const size = data.size_system.eu[index];
  const first = anchors[0], last = anchors[anchors.length - 1];
  if (size <= first.eu) return { ...first.ease_cm };
  if (size >= last.eu) return { ...last.ease_cm };
  const upper = anchors.findIndex(a => a.eu >= size);
  const left = anchors[upper - 1], right = anchors[upper];
  const fraction = (size - left.eu) / (right.eu - left.eu);
  return Object.fromEntries(BODY_KEYS.map(key => [key, left.ease_cm[key] + fraction * (right.ease_cm[key] - left.ease_cm[key])]));
 }
 function finishedAt(data, index, keys) {
  const columns = { bust: 'bust_circumference', waist: 'waist_circumference', hip: 'hip_hem_circumference', upper_arm: 'upper_arm_circumference' };
  return Object.fromEntries(keys.map(key => [key, data.finished_measurements_cm[columns[key]][index]]));
 }
 function compare(data, entered, unit = 'cm') {
  const keys = [...BODY_KEYS], cm = {};
  if (String(entered.upper_arm ?? '').trim() !== '') keys.push('upper_arm');
  for (const key of keys) {
   const n = parseMeasurement(entered[key]);
   if (n === null) return { status: 'invalid', field: key };
   cm[key] = n * (unit === 'in' ? 2.54 : 1);
  }
  // This tool's supported entries, not universal anatomical standards.
  const ranges = { bust: [70, 200], waist: [50, 200], hip: [70, 220], upper_arm: [15, 80] };
  const exclusiveLowerBounds = new Set(BODY_KEYS);
  const checkFields = keys.filter(key => (exclusiveLowerBounds.has(key) ? cm[key] <= ranges[key][0] + EPS : cm[key] < ranges[key][0] - EPS) || cm[key] > ranges[key][1] + EPS);
  if (checkFields.length) return { status: 'invalid_measurements', fields: checkFields, cm };
  // Waist/hip typo check is independent of bust size; bust/hip differences do not block an entry.
  if (cm.waist < cm.hip * 0.45 - EPS || cm.waist > cm.hip * 1.35 + EPS) {
   return { status: 'invalid_measurements', reason: 'proportions', fields: ['waist', 'hip'], cm };
  }
  // Keep the upper bust-target limit. At the lower edge, rank the offered sizes; never invent EU 30.
  const target = cm.bust + data.supported_bust_target_room_cm;
  const bustValues = data.finished_measurements_cm.bust_circumference;
  if (target > bustValues[bustValues.length - 1] + EPS) return { status: 'no_match', cm, target };
  const minimumBodyRoom = data.minimum_body_room_cm;
  const candidates = data.size_system.eu.map((eu, index) => {
   const finishedByField = finishedAt(data, index, keys);
   const room = Object.fromEntries(keys.map(key => [key, finishedByField[key] - cm[key]]));
   const reference = referenceEase(data, index);
   const allowed = room.bust > EPS && room.waist >= minimumBodyRoom.waist - EPS && room.hip >= minimumBodyRoom.hip - EPS;
   const score = BODY_KEYS.reduce((total, key) => total + Math.abs(room[key] - reference[key]), 0);
   return { index, eu, us: data.size_system.us[index], finishedByField, room, reference, allowed, score };
  });
  const available = candidates.filter(c => c.allowed);
  if (!available.length) {
   const last = candidates[candidates.length - 1];
   return { status: 'no_match', cm, target, room: last.room,
    fields: BODY_KEYS.filter(key => key === 'bust' ? last.room[key] <= EPS : last.room[key] < minimumBodyRoom[key] - EPS),
    lowRoomFields: BODY_KEYS.filter(key => last.room[key] <= 10 + EPS) };
  }
  // Joint L1 comparison. Tie: larger size; no switching between two independent body profiles.
  let selected = available[0];
  for (const candidate of available.slice(1)) {
   if (candidate.score < selected.score - EPS || Math.abs(candidate.score - selected.score) <= EPS) selected = candidate;
  }
  const bustDistances = bustValues.map(value => Math.abs(value - target));
  const nearestBustDistance = Math.min(...bustDistances);
  const bustIndex = bustDistances.reduce((last, distance, index) => Math.abs(distance - nearestBustDistance) <= EPS ? index : last, 0);
  const modelBustRoom = data.reference_fit.anchors[0].ease_cm.bust;
  const reducedBustRoom = selected.room.bust < modelBustRoom - EPS;
  const armNeedsReview = cm.upper_arm !== undefined && selected.room.upper_arm < data.minimum_upper_arm_room_cm - EPS;
  const lowRoomFields = BODY_KEYS.filter(key => selected.room[key] <= 10 + EPS);
  // The look guard applies after ranking; never silently size up to evade it.
  const bustFitStop = selected.room.bust < data.minimum_auto_bust_room_cm - EPS;
  const next = bustFitStop
   ? candidates.find(candidate => candidate.index > selected.index && candidate.allowed && candidate.room.bust >= data.minimum_auto_bust_room_cm - EPS)
   : candidates[selected.index + 1];
  const alternative = reducedBustRoom && next?.allowed ? {
   eu: next.eu, us: next.us, room: next.room, finishedByField: next.finishedByField
  } : null;
  return {
   status: bustFitStop ? 'fit_stop' : 'match', minimumAutoBustRoom: data.minimum_auto_bust_room_cm, method: data.reference_fit.method, index: selected.index, eu: selected.eu, us: selected.us,
   cm, target, finished: selected.finishedByField.bust, finishedByField: selected.finishedByField, room: selected.room,
   expectedEase: selected.reference, fitScore: selected.score, modelBustRoom,
   reducedBustRoom, armNeedsReview, lowRoomFields, alternative, smallestSize: selected.index === 0,
   comparisonOnly: reducedBustRoom || armNeedsReview || lowRoomFields.length > 0,
   adjustedForBody: selected.index > bustIndex,
   referenceEase: modelBustRoom, exact: selected.score <= EPS,
   tied: available.filter(c => Math.abs(c.score - selected.score) <= EPS).length > 1
  };
 }
 root.BarbaraSizeFinderEngine = Object.freeze({ parseMeasurement, compare, referenceEase });
})(globalThis);

